import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { useNotificationCount } from '@/hooks/useNotificationCount';
import { useNoteFeed } from '@/hooks/useNoteFeed';
import { Layout } from '@/components/Layout';
import { NoteComposer } from '@/components/NoteComposer';
import { NoteCard, NoteCardSkeleton } from '@/components/NoteCard';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { nip57 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';

/* ── helpers ─────────────────────────────────────────────────────────────── */

function timeAgo(ts: number): string {
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(ts * 1000).toLocaleDateString();
}

function extractZapAmount(event: NostrEvent): number {
  const bolt11 = event.tags.find(([n]) => n === 'bolt11')?.[1];
  if (bolt11) {
    try { return nip57.getSatoshisAmountFromBolt11(bolt11); } catch { /* fall through */ }
  }
  const amountTag = event.tags.find(([n]) => n === 'amount')?.[1];
  if (amountTag) return Math.floor(parseInt(amountTag) / 1000);
  return 0;
}

function getSenderPubkey(event: NostrEvent): string {
  if (event.kind === 9735) {
    const desc = event.tags.find(([n]) => n === 'description')?.[1];
    if (desc) {
      try {
        const req = JSON.parse(desc);
        if (req.pubkey) return req.pubkey;
      } catch { /* fall through */ }
    }
  }
  return event.pubkey;
}

type NotifKind = 'zap' | 'reaction' | 'mention';

interface NotifEvent {
  event: NostrEvent;
  kind: NotifKind;
  senderPubkey: string;
}

/* ── Notification row ─────────────────────────────────────────────────────── */

function NotifRow({ item }: { item: NotifEvent }) {
  const author = useAuthor(item.senderPubkey);
  const meta = author.data?.metadata;
  const displayName = meta?.name ?? meta?.display_name ?? item.senderPubkey.slice(0, 8) + '…';

  const icon = item.kind === 'zap'
    ? <span className="text-lg">⚡</span>
    : item.kind === 'reaction'
    ? <span className="text-lg">{item.event.content || '❤️'}</span>
    : <span className="text-lg">💬</span>;

  const label = item.kind === 'zap'
    ? `zapped ${extractZapAmount(item.event).toLocaleString()} sats`
    : item.kind === 'reaction'
    ? 'reacted to your content'
    : 'mentioned you';

  const excerpt = item.kind === 'mention'
    ? item.event.content.slice(0, 120) + (item.event.content.length > 120 ? '…' : '')
    : null;

  const linkedId = item.event.tags.find(([n]) => n === 'e')?.[1];

  return (
    <div
      className="flex items-start gap-3 px-4 py-3 transition-colors"
      style={{ borderBottom: '1px solid var(--gb-border)' }}
    >
      <Link to={`/profile/${item.senderPubkey}`} className="shrink-0">
        <Avatar className="h-9 w-9">
          <AvatarImage src={meta?.picture} />
          <AvatarFallback
            className="text-xs font-bold"
            style={{ background: 'var(--gb-accent)', color: '#fff' }}
          >
            {displayName[0]?.toUpperCase() ?? '?'}
          </AvatarFallback>
        </Avatar>
      </Link>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          {icon}
          <Link
            to={`/profile/${item.senderPubkey}`}
            className="text-sm font-semibold no-underline hover:underline"
            style={{ color: 'var(--text-primary)' }}
          >
            {displayName}
          </Link>
          <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{label}</span>
        </div>
        {excerpt && (
          <p className="text-xs mt-1 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>
            {excerpt}
          </p>
        )}
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            {timeAgo(item.event.created_at)}
          </span>
          {linkedId && (
            <Link
              to={`/video/${linkedId}`}
              className="text-xs hover:underline"
              style={{ color: 'var(--gb-accent)' }}
            >
              View video →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function NotifSkeleton() {
  return (
    <div className="flex items-start gap-3 px-4 py-3" style={{ borderBottom: '1px solid var(--gb-border)' }}>
      <Skeleton className="h-9 w-9 rounded-full shrink-0" style={{ background: 'var(--bg-secondary)' }} />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-3/4" style={{ background: 'var(--bg-secondary)' }} />
        <Skeleton className="h-3 w-1/2" style={{ background: 'var(--bg-secondary)' }} />
      </div>
    </div>
  );
}

/* ── Note Feed tab ────────────────────────────────────────────────────────── */

function NoteFeedTab() {
  const { user, metadata } = useCurrentUser();
  const { data: notes, isLoading } = useNoteFeed(60);
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());

  const visibleNotes = useMemo(
    () => (notes ?? []).filter(n => !deletedIds.has(n.id)),
    [notes, deletedIds],
  );

  if (!user) {
    return (
      <div
        className="mx-4 mt-8 rounded-xl py-16 px-8 text-center"
        style={{ border: '2px dashed var(--gb-border)' }}
      >
        <div className="text-5xl mb-4">📝</div>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Log in to see and post notes.
        </p>
      </div>
    );
  }

  const displayName = metadata?.name ?? metadata?.display_name ?? 'You';

  return (
    <div>
      {/* Compose box */}
      <div
        className="px-4 py-4"
        style={{ borderBottom: '1px solid var(--gb-border)' }}
      >
        <NoteComposer
          placeholder={`What's on your mind, ${displayName}?`}
          onPublished={() => {
            // Feed will invalidate via the hook
          }}
        />
      </div>

      {/* Feed */}
      {isLoading && (
        <div>
          {Array.from({ length: 6 }).map((_, i) => <NoteCardSkeleton key={i} />)}
        </div>
      )}

      {!isLoading && visibleNotes.length === 0 && (
        <div
          className="mx-4 mt-8 rounded-xl py-16 px-8 text-center"
          style={{ border: '2px dashed var(--gb-border)' }}
        >
          <div className="text-5xl mb-4">📭</div>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            No notes yet from people you follow.
            <br />
            Follow some users or post your own note above!
          </p>
        </div>
      )}

      {!isLoading && visibleNotes.length > 0 && (
        <div>
          {visibleNotes.map(note => (
            <NoteCard
              key={note.id}
              event={note}
              onDeleted={id => setDeletedIds(prev => new Set([...prev, id]))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Page ─────────────────────────────────────────────────────────────────── */

type Tab = 'notifications' | 'feed';

export default function NotificationsPage() {
  useSeoMeta({ title: 'Notifications – Goblinbox' });

  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { markAllRead } = useNotificationCount();
  const [activeTab, setActiveTab] = useState<Tab>('notifications');

  // Mark all read when the notifications tab is viewed
  useEffect(() => {
    if (activeTab === 'notifications') markAllRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const since = Math.floor(Date.now() / 1000) - 30 * 24 * 60 * 60;

  const { data: events, isLoading } = useQuery<NostrEvent[]>({
    queryKey: ['nostr', 'notifications', user?.pubkey ?? ''],
    queryFn: async ({ signal }) => {
      if (!user) return [];
      return nostr.query(
        [
          { kinds: [9735], '#p': [user.pubkey], since, limit: 100 },
          { kinds: [7],    '#p': [user.pubkey], since, limit: 100 },
          { kinds: [1],    '#p': [user.pubkey], since, limit: 100 },
        ],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(8000)]) },
      );
    },
    enabled: !!user,
    staleTime: 60 * 1000,
  });

  const items: NotifEvent[] = useMemo(() => {
    if (!events) return [];
    return events
      .map((event): NotifEvent => ({
        event,
        kind: event.kind === 9735 ? 'zap' : event.kind === 7 ? 'reaction' : 'mention',
        senderPubkey: getSenderPubkey(event),
      }))
      .filter(item => item.senderPubkey !== user?.pubkey)
      .sort((a, b) => b.event.created_at - a.event.created_at);
  }, [events, user?.pubkey]);

  return (
    <Layout>
      <div style={{ maxWidth: 680, margin: '0 auto' }}>

        {/* Sticky header with tabs */}
        <div
          className="sticky top-[56px] sm:top-[60px] z-10"
          style={{
            background: 'var(--bg-primary)',
            borderBottom: '1px solid var(--gb-border)',
          }}
        >
          {/* Title row */}
          <div className="flex items-center justify-between px-4 pt-3 pb-0">
            <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
              {activeTab === 'notifications' ? 'Notifications' : 'Notes'}
            </h1>
            {activeTab === 'notifications' && items.length > 0 && (
              <span
                className="text-xs px-2 py-0.5 rounded-full"
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}
              >
                {items.length}
              </span>
            )}
          </div>

          {/* Tab bar */}
          <div className="flex px-4">
            <TabButton
              active={activeTab === 'notifications'}
              onClick={() => setActiveTab('notifications')}
            >
              Notifications
            </TabButton>
            <TabButton
              active={activeTab === 'feed'}
              onClick={() => setActiveTab('feed')}
            >
              Notes Feed
            </TabButton>
          </div>
        </div>

        {/* ── Notifications tab ─── */}
        {activeTab === 'notifications' && (
          <>
            {!user && (
              <EmptyState emoji="🔔" message="Log in to see your notifications." />
            )}
            {user && isLoading && (
              <div>{Array.from({ length: 8 }).map((_, i) => <NotifSkeleton key={i} />)}</div>
            )}
            {user && !isLoading && items.length === 0 && (
              <EmptyState emoji="🔔" message="No notifications in the last 30 days." />
            )}
            {user && !isLoading && items.length > 0 && (
              <div>
                {items.map(item => (
                  <NotifRow key={item.event.id} item={item} />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Notes Feed tab ─── */}
        {activeTab === 'feed' && <NoteFeedTab />}

      </div>
    </Layout>
  );
}

/* ── Small helpers ─────────────────────────────────────────────────────────── */

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-4 py-2.5 text-sm font-medium transition-colors relative"
      style={{
        color: active ? 'var(--gb-accent)' : 'var(--text-secondary)',
        background: 'none',
        border: 'none',
        borderBottom: active ? '2px solid var(--gb-accent)' : '2px solid transparent',
        cursor: 'pointer',
        marginBottom: -1,
      }}
    >
      {children}
    </button>
  );
}

function EmptyState({ emoji, message }: { emoji: string; message: string }) {
  return (
    <div
      className="mx-4 mt-8 rounded-xl py-16 px-8 text-center"
      style={{ border: '2px dashed var(--gb-border)' }}
    >
      <div className="text-5xl mb-4">{emoji}</div>
      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{message}</p>
    </div>
  );
}
